import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { signIn } from "../../../core/data/redux/auth/authAction"; // Import the signIn action
import { AppDispatch } from "../../../core/data/redux/store";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup"; // For validation schema
import { message, Modal, Progress, Spin } from "antd";
import { getUserMenuPermissions } from "../../../core/data/redux/user/rolePermission/rolePermissionAction";
import { fetchActiveCompanyForUser } from "../../../core/data/redux/company/companyDetailsActions";
import axios from "axios";
import operationServiceApiInstance from "../../../core/config/openApionfig";

// Partner API URL for fetching subscription details
const PARTNER_API_BASE_URL = "https://partnerapi.leadchainsystems.in";

type PasswordField = "password";

interface MigrationStatus {
  isChecking: boolean;
  isRunning: boolean;
  needsMigration: boolean;
  currentVersion: string;
  targetVersion: string;
  pendingMigrations: string[];
  progress: number;
  error: string | null;
}

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { signInLoading } = useSelector((state: any) => state.auth);

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
  });

  const [rememberMe, setRememberMe] = useState(false);
  const [migrationModalVisible, setMigrationModalVisible] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus>({
    isChecking: false,
    isRunning: false,
    needsMigration: false,
    currentVersion: "",
    targetVersion: "",
    pendingMigrations: [],
    progress: 0,
    error: null,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // Redirect to dashboard after successful login
  // useEffect(() => {
  //   if (token && userMenuPermissions) {
  //     const isAdminAllowed = Object.keys(userMenuPermissions?.menuPermissions || {}).includes("Admin Dashboard");
  //     navigate(isAdminAllowed ? routes.adminDashboard : routes.employeeDashboard);
  //   }
  // }, [token, navigate, routes.adminDashboard, routes.employeeDashboard, userMenuPermissions]);

  // Formik validation schema
  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email address").required("Email is required"),
    password: Yup.string().min(6, "Password must be at least 6 characters").required("Password is required"),
    subscription_id: Yup.string()
    .matches(/^\d+$/, "Subscription ID must be numeric")
    .required("Subscription ID is required"),
  });

  // Check localStorage for saved credentials and Remember Me state
  useEffect(() => {
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");
    const savedSubscriptionId = localStorage.getItem("subscription_id");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true"; 

    if (savedEmail && savedPassword && savedSubscriptionId) {
      setRememberMe(savedRememberMe);
    }
  }, []);

  // Fetch subscription details from Partner API (client-side)
  const fetchSubscriptionDetails = async (subscription_id: string, email: string) => {
    try {
      const response = await axios.get(
        `${PARTNER_API_BASE_URL}/api/v1/subscription_detail/${subscription_id}/${email}`
      );
      return response.data;
    } catch (error: any) {
      console.error("Failed to fetch subscription details:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch subscription details");
    }
  };

  // Check if migration is needed
  const checkMigration = async (subscription_id: string) => {
    try {
      const response = await operationServiceApiInstance.post('/auth/check-migration', {
        subscription_id,
      });
      return response.data.data;
    } catch (error: any) {
      console.error("Failed to check migration:", error);
      throw new Error(error.response?.data?.error || "Failed to check migration status");
    }
  };

  // Run database migration
  const runMigration = async (subscription_id: string) => {
    try {
      const response = await operationServiceApiInstance.post('/auth/run-migration', {
        subscription_id,
      });
      return response.data.data;
    } catch (error: any) {
      console.error("Migration failed:", error);
      throw new Error(error.response?.data?.error || "Migration failed");
    }
  };

  // Handle migration execution
  const handleRunMigration = async (subscription_id: string, pendingValues: any) => {
    setMigrationStatus(prev => ({ ...prev, isRunning: true, progress: 10, error: null }));
    
    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setMigrationStatus(prev => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90),
        }));
      }, 500);

      const result = await runMigration(subscription_id);
      
      clearInterval(progressInterval);
      setMigrationStatus(prev => ({ ...prev, progress: 100, isRunning: false }));
      
      message.success("Database migration completed successfully!");
      setMigrationModalVisible(false);
      
      // Reset migration status
      setMigrationStatus({
        isChecking: false,
        isRunning: false,
        needsMigration: false,
        currentVersion: result.finalVersion || "",
        targetVersion: result.finalVersion || "",
        pendingMigrations: [],
        progress: 0,
        error: null,
      });
      
      // Now proceed with login using stored values
      if (pendingValues) {
        await performLogin(pendingValues, pendingSubscriptionData);
      }
    } catch (error: any) {
      setMigrationStatus(prev => ({
        ...prev,
        isRunning: false,
        error: error.message || "Migration failed",
      }));
      message.error(`Migration failed: ${error.message}`);
    }
  };

  // Stored values for after migration
  const [pendingLoginValues, setPendingLoginValues] = useState<any>(null);
  const [pendingSubscriptionData, setPendingSubscriptionData] = useState<any>(null);

  // Perform the actual login with subscription data
  const performLogin = async (values: { email: string; password: string; subscription_id: string }, subscriptionDataParam?: any) => {
    try {
      // Use passed subscription data or fall back to stored state
      const subscriptionData = subscriptionDataParam || pendingSubscriptionData;
      
      if (!subscriptionData) {
        message.error("Subscription data not available. Please try again.");
        return;
      }

      const response = await dispatch(
        signIn({
          email: values.email,
          password: values.password,
          subscription_id: values.subscription_id,
          configID: "2",
          subscription_data: subscriptionData.message?.[0] || subscriptionData,
        })
      );
  
      if (signIn.fulfilled.match(response)) {
        const data: any = response.payload.data;
        
        // Check if there's an existing session that needs confirmation
        if (data.hasExistingSession && data.requireLogoutConfirmation) {
          Modal.confirm({
            title: "Active Session Detected",
            content: "You're already logged in on another device. Do you want to logout from the other session?",
            okText: "Logout Other Session",
            cancelText: "Cancel",
            onOk: async () => {
              try {
                console.log("[performLogin] Logging out other session...", { 
                  email: values.email, 
                  subscription_id: values.subscription_id, 
                  company_code: data.company_code 
                });
                
                const logoutResponse = await operationServiceApiInstance.post('/auth/logout-other-session', {
                  email: values.email,
                  subscription_id: values.subscription_id,
                  company_code: data.company_code,
                });
                
                console.log("[performLogin] Logout response:", logoutResponse.data);
                
                if (logoutResponse.data?.status) {
                  message.success("Logged out from previous session. Trying login again...");
                  await performLogin(values, subscriptionData);
                } else {
                  message.error("Failed to logout previous session.");
                }
              } catch (err: any) {
                console.error("[performLogin] Logout error:", err);
                message.error(err?.response?.data?.error || "Failed to logout previous session.");
              }
            },
          });
          return;
        }
        
        // Store session ID first
        localStorage.setItem("session_id", data.session_id);
        
        // Fetch active company and wait for the response
        const activeCompanyResponse: any = await dispatch(fetchActiveCompanyForUser(data.UserId));
        console.log({response, activeCompanyResponse});
        
        // Store active company in localStorage so it persists across page reload
        if (fetchActiveCompanyForUser.fulfilled.match(activeCompanyResponse)) {
          localStorage.setItem("activeCompany", JSON.stringify(activeCompanyResponse.payload?.data));
        }
  
        // Handle remember me
        if (rememberMe) {
          localStorage.setItem("email", values.email);
          localStorage.setItem("password", btoa(values.password));
          localStorage.setItem("subscription_id", values.subscription_id);
          localStorage.setItem("configID", "2");
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
          localStorage.removeItem("subscription_id");
          localStorage.removeItem("configID");
          localStorage.removeItem("rememberMe");
        }
  
        // Get permissions
        const permissionsResponse:any = await dispatch(getUserMenuPermissions());
        const menuPermissions = permissionsResponse?.payload?.data?.menuPermissions || {};
        const isAdminAllowed = Object.keys(menuPermissions).includes("Admin Dashboard");
        
        // Subscription warning
        if (data?.checkRenewStatus) {
          Modal.warning({
            title: 'Subscription Warning',
            content: data?.configError,
            okText: 'OK',
            onOk: () => {
              window.location.href = isAdminAllowed
                ? all_routes.adminDashboard
                : all_routes.employeeDashboard;
            },
          });
          return;
        }
  
        message.success("Login successful!");
        window.location.href = isAdminAllowed
          ? all_routes.adminDashboard
          : all_routes.employeeDashboard;
  
      } else {
        handleLoginError(response, values, subscriptionData);
      }
    } catch (error) {
      console.error("Login error:", error);
      message.error("Unexpected error during login.");
    }
  };

  // Handle login errors
  const handleLoginError = (response: any, values: any, subscriptionData?: any) => {
    const errorMessage = response?.payload?.error || response?.payload || "Login failed. Please try again.";
    message.error(`Login failed: ${errorMessage}`);
  };

  const handleSubmit = async (values: { email: string; password: string; subscription_id: string }) => {
    try {
      setSubscriptionLoading(true);
      
      // Step 1: Fetch subscription details from Partner API (client-side call)
      const subscriptionResponse = await fetchSubscriptionDetails(values.subscription_id, values.email);
      
      if (!subscriptionResponse.status) {
        message.error(subscriptionResponse.message || "Invalid subscription");
        setSubscriptionLoading(false);
        return;
      }

      const subscriptionData = subscriptionResponse.message?.[0];
      if (!subscriptionData) {
        message.error("Invalid subscription or missing data");
        setSubscriptionLoading(false);
        return;
      }

      // Store subscription data for later use
      setPendingSubscriptionData(subscriptionResponse);
      setPendingLoginValues(values);

      // Step 2: Check if migration is needed
      setMigrationStatus(prev => ({ ...prev, isChecking: true }));
      
      try {
        const migrationCheck = await checkMigration(values.subscription_id);
        
        setMigrationStatus(prev => ({
          ...prev,
          isChecking: false,
          needsMigration: migrationCheck.needsMigration,
          currentVersion: migrationCheck.currentVersion,
          targetVersion: migrationCheck.targetVersion,
          pendingMigrations: migrationCheck.pendingMigrations || [],
        }));

        if (migrationCheck.needsMigration) {
          // Show migration modal - user must complete migration before login
          setMigrationModalVisible(true);
          setSubscriptionLoading(false);
          return;
        }
      } catch (migrationError: any) {
        // If migration check fails, continue with login (backwards compatibility)
        console.warn("Migration check failed, proceeding with login:", migrationError);
        setMigrationStatus(prev => ({ ...prev, isChecking: false }));
      }

      setSubscriptionLoading(false);
      
      // Step 3: Proceed with optimized login (pass subscriptionResponse directly)
      await performLogin(values, subscriptionResponse);
      
    } catch (error: any) {
      console.error("Login process error:", error);
      message.error(error.message || "Unexpected error during login.");
      setSubscriptionLoading(false);
      setMigrationStatus(prev => ({ ...prev, isChecking: false }));
    }
  };
  

  const handleRememberMeChange = () => {
    const newRememberMe = !rememberMe;
    setRememberMe(newRememberMe);
    localStorage.setItem("rememberMe", newRememberMe.toString()); // Save the state of the checkbox in localStorage
  };

  // Clear local storage on logout
  // const handleLogout = () => {
  //   localStorage.removeItem("email");
  //   localStorage.removeItem("password");
  //   localStorage.removeItem("subscription_id");
  //   localStorage.removeItem("configID");
  //   localStorage.removeItem("rememberMe");
  // };

  return (
    <div className="container-fuild">
      <div className="w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
        <div className="row">
          <div className="col-lg-5">
            <div className="login-background position-relative d-lg-flex align-items-center justify-content-center d-none flex-wrap vh-100">
              <div className="bg-overlay-img">
                <ImageWithBasePath src="assets/img/bg/bg-01.png" className="bg-1" alt="Img" />
                <ImageWithBasePath src="assets/img/bg/bg-02.png" className="bg-2" alt="Img" />
                <ImageWithBasePath src="assets/img/bg/bg-03.png" className="bg-3" alt="Img" />
              </div>
              <div className="authentication-card w-100">
                <div className="authen-overlay-item border w-100">
                  <h1 className="text-white display-1">
                    Empowering people <br /> through seamless HR <br /> management.
                  </h1>
                  <div className="my-4 mx-auto authen-overlay-img">
                    <ImageWithBasePath src="assets/img/bg/authentication-bg-001.svg" alt="Img" />
                  </div>
                  <div>
                    <p className="text-white fs-20 fw-semibold text-center">
                      Efficiently manage your workforce, streamline <br />{" "}
                      operations effortlessly.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-7 col-md-12 col-sm-12">
            <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap">
              <div className="col-md-7 mx-auto vh-100">
                <Formik
                  initialValues={{
                    email: localStorage.getItem("email") || "",
                    password: localStorage.getItem("password") ? atob(localStorage.getItem("password")!) : "",
                    subscription_id: localStorage.getItem("subscription_id") || "",
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ touched, errors, isSubmitting }) => (
                    <Form className="vh-100 d-flex flex-column justify-content-between p-4 pb-0">
                      <div className=" mx-auto mb-5 text-center">
                        <ImageWithBasePath
                          src="assets/img/hrmlogo24.svg"
                          className="img-fluid"
                          alt="Logo"
                          style={{ width: '180  px', height: 'auto', marginLeft: '' }}
                        />
                      </div>
                      <div className="">
                        <div className="text-center mb-3">
                          <h2 className="mb-2">Sign In</h2>
                          <p className="mb-0">Please enter your details to sign in</p>
                        </div>

                        {/* Subscription ID */}
                        <div className="mb-3">
                          <label className="form-label">Subscription Id</label>
                          <div className="input-group">
                            <Field
                              type="text"
                              name="subscription_id"
                              className="form-control border-end-0"
                            />
                          </div>
                          <ErrorMessage
                            name="subscription_id"
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        {/* Email */}
                        <div className="mb-3">
                          <label className="form-label">Email Address</label>
                          <div className="input-group">
                            <Field
                              type="text"
                              name="email"
                              className="form-control border-end-0"
                            />
                          </div>

                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        {/* Password */}
                        <div className="mb-3">
                          <label className="form-label">Password</label>
                          <div className="pass-group">
                            <Field
                              type={passwordVisibility.password ? "text" : "password"}
                              name="password"
                              className="pass-input form-control"
                            />
                            <span
                              className={`ti toggle-passwords ${passwordVisibility.password ? "ti-eye" : "ti-eye-off"}`}
                              onClick={() => togglePasswordVisibility("password")}
                            ></span>
                          </div>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="text-danger"
                          />
                        </div>

                        {/* Remember me and Forgot password link */}
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center">
                            <div className="form-check form-check-md mb-0">
                              <input
                                className="form-check-input"
                                id="remember_me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={handleRememberMeChange} 
                              />
                              <label
                                htmlFor="remember_me"
                                className="form-check-label mt-0"
                              >
                                Remember Me
                              </label>
                            </div>
                          </div>
                          <div className="text-end">
                            <Link to={all_routes.forgotPassword} className="link-danger">
                              Forgot Password?
                            </Link>
                          </div>
                        </div>

                        {/* Sign In Button */}
                        <div className="mb-3">
                          <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={isSubmitting || signInLoading || subscriptionLoading || migrationStatus.isChecking}
                          >
                            {subscriptionLoading 
                              ? "Validating Subscription..." 
                              : migrationStatus.isChecking 
                                ? "Checking Database..." 
                                : signInLoading 
                                  ? "Signing In..." 
                                  : "Sign In"}
                          </button>
                        </div>

                          {/* href="https://register.hrmtree.com/?productId=12" */}

                        {/* Register link */}
                        <div className="text-center">
                          <h6 className="fw-normal text-dark mb-0">
                            Don’t have an account?
                            <a
                              href="https://register.leadchainsystems.com/?productId=13"
                              rel="noopener noreferrer"
                              className="hover-a"
                            >
                              Create Account
                            </a>
                          </h6>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-5 pb-4 text-center">
                        <p className="mb-0 text-gray-9">Copyright © 2025 - hrmtree</p>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Modal - Cannot be closed until migration is complete */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🔄</span>
            <span>Update Version</span>
          </div>
        }
        open={migrationModalVisible}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        centered
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          {!migrationStatus.isRunning ? (
            <>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  Your database needs to be updated before you can login. This is required to ensure compatibility with the latest features.
                </p>
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: '15px', 
                  borderRadius: '8px',
                  marginBottom: '15px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Current Version:</span>
                    <span style={{ color: '#ff4d4f' }}>{migrationStatus.currentVersion || 'Unknown'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Target Version:</span>
                    <span style={{ color: '#52c41a' }}>{migrationStatus.targetVersion || 'Latest'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 500 }}>Pending Updates:</span>
                    <span>{migrationStatus.pendingMigrations?.length || 0} migration(s)</span>
                  </div>
                </div>
                {migrationStatus.error && (
                  <div style={{ 
                    background: '#fff2f0', 
                    border: '1px solid #ffccc7',
                    padding: '10px', 
                    borderRadius: '4px',
                    color: '#ff4d4f',
                    marginBottom: '15px'
                  }}>
                    <strong>Error:</strong> {migrationStatus.error}
                  </div>
                )}
              </div>
              <button
                className="btn btn-primary w-100"
                onClick={() => handleRunMigration(pendingLoginValues?.subscription_id, pendingLoginValues)}
                disabled={migrationStatus.isRunning}
                style={{ height: '45px', fontSize: '16px' }}
              >
                Update Version
              </button>
              <p style={{ 
                textAlign: 'center', 
                marginTop: '15px', 
                fontSize: '12px', 
                color: '#999' 
              }}>
                ⚠️ Please do not close this window during migration
              </p>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>
                Migration in Progress...
              </h4>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Please wait while we update your database. This may take a few moments.
              </p>
              <Progress 
                percent={migrationStatus.progress} 
                status="active"
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <p style={{ 
                marginTop: '15px', 
                fontSize: '12px', 
                color: '#ff4d4f',
                fontWeight: 500
              }}>
                ⚠️ Do not close or refresh this page
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Login;
